import React, { useState } from "react";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Loader2,
} from "lucide-react";
import Swal from 'sweetalert2'
import api from "../../utils/axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";


const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();



const handleLogin = async () => {
  if (!email || !password) {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Please enter both email and password.",
    });
    return;
  }

  setLoading(true);

  try {
    const res = await api.post("/login", { email, password });

    console.log("Login response:", res.data); // Debugging log

    if (res.data.token) {
      localStorage.setItem("token", res.data.token);
      const userData = res.data.data || {};
      const role = (userData.role || "ADMIN").toUpperCase();
      const id = userData.id;

      login(userData);

      Swal.fire({
        icon: "success",
        title: "Login Successful",
        text: role === "AGENT" ? "Welcome to your Agent Portal." : `Welcome back, ${userData.username || "Admin"}.`,
      }).then(() => {
        if (role === "AGENT") {
          navigate("/agent/dashboard");
        } else {
          navigate("/");
        }
      });
    }
  } catch (error: any) {
    Swal.fire({
      icon: "error",
      title: "Login Failed",
      text:
        error.response?.data?.message ||
        "An error occurred during login.",
    });
  } finally {
    setLoading(false);
  }
};


  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-200 via-green-100 to-white px-4">
      {/* Login Card */}
      <div className="w-full max-w-md bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl border border-white p-8 text-center">
        {/* Icon */}
        <div className="mx-auto mb-4 w-12 h-12 rounded-2xl bg-white shadow flex items-center justify-center">
          <ArrowRight size={22} />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-semibold text-gray-800">
          Sign in with email
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          Access your admin dashboard securely
        </p>

        {/* Email */}
        <div className="mt-6 relative">
          <Mail
            size={18}
            className="absolute left-4 top-3.5 text-gray-400"
          />
          <input
            type="email"
            placeholder="Email"
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-gray-100 border focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {/* Password */}
        <div className="mt-4 relative">
          <Lock
            size={18}
            className="absolute left-4 top-3.5 text-gray-400"
          />
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            className="w-full pl-11 pr-10 py-3 rounded-xl bg-gray-100 border focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? (
              <EyeOff size={18} />
            ) : (
              <Eye size={18} />
            )}
          </button>
        </div>

        {/* Forgot */}
        <div className="text-right mt-2">
          <button className="text-xs text-gray-500 hover:underline">
            Forgot password?
          </button>
        </div>

        {/* Button */}
        <button
          onClick={handleLogin}
          disabled={loading}
          className={`mt-6 w-full py-3 rounded-xl font-semibold shadow transition
            ${loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-gradient-to-b from-gray-900 to-black hover:opacity-90 text-white"
            }`}
        >
          {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="animate-spin" size={18} />
                Logging in...
              </span>
            ) : (
              "Get Started"
            )}

        </button>


        {/* Divider */}
        <div className="flex items-center gap-2 my-6">
          <div className="flex-1 h-px bg-gray-300" />
          <span className="text-xs text-gray-500">
            Or sign in with
          </span>
          <div className="flex-1 h-px bg-gray-300" />
        </div>

        {/* Social (UI only) */}
        <div className="flex justify-center gap-4">
          {["G", "F", ""].map((i) => (
            <div
              key={i}
              className="w-12 h-10 rounded-xl bg-white shadow border flex items-center justify-center text-gray-700 font-semibold"
            >
              {i}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
